'use client';

import React, { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { useLocalization } from '@akinon/next/hooks';
import { Button, Icon } from '@theme/components';
import clsx from 'clsx';
import BlogItem from './blog-item';

export default function BlogRecentSlider({ blogs }: { blogs: any[] }) {
  const { t } = useLocalization();
  const [progress, setProgress] = useState(0);
  const carouselRef = useRef<any>(null);
  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const [nav, setNav] = useState({
    isBeginning: true,
    isEnd: false,
    isLocked: true
  });
  const [arrowTop, setArrowTop] = useState<number | null>(null);

  const updateNav = (swiper: any) => {
    setNav({
      isBeginning: swiper.isBeginning,
      isEnd: swiper.isEnd,
      isLocked: swiper.isLocked
    });

    const image = sliderContainerRef.current?.querySelector(
      'img'
    ) as HTMLElement | null;
    const height = image?.getBoundingClientRect().height ?? 0;
    if (height > 0) setArrowTop(height / 2);
  };

  const goPrev = () => carouselRef.current?.swiper?.slidePrev();
  const goNext = () => carouselRef.current?.swiper?.slideNext();

  if (!blogs?.length) return null;

  return (
    <div className="max-w-[1280px] mx-auto py-12 lg:py-16">
      <h2 className="px-5 mb-8 lg:mb-10 text-2xl lg:text-[32px] leading-[110%] font-medium text-black-100">
        {t('common.blog.recent_posts')}
      </h2>

      <div className="px-5">
        <div className="flex items-center relative">
          <Button
            type="button"
            onClick={goPrev}
            disabled={nav.isBeginning}
            aria-label={t('common.slider.prev')}
            style={{ top: arrowTop ?? '50%' }}
            className={clsx(
              'absolute left-1 sm:left-2 -translate-y-1/2 z-[2] w-9 h-9 sm:w-11 sm:h-11 !p-0 flex items-center justify-center bg-white border border-gray-250 shadow-md rounded-full hover:bg-white disabled:opacity-0 disabled:pointer-events-none transition-opacity',
              nav.isLocked && 'hidden'
            )}
          >
            <Icon name="chevron-start" className="text-black text-lg" />
          </Button>

          <div ref={sliderContainerRef} className="w-full overflow-hidden">
            <Swiper
              ref={carouselRef}
              slidesPerView={1.5}
              spaceBetween={10}
              breakpoints={{
                640: { slidesPerView: 2.2, spaceBetween: 20 },
                768: { slidesPerView: 3, spaceBetween: 40 },
                1024: { slidesPerView: 4, spaceBetween: 40 }
              }}
              onProgress={(_, prog) => setProgress(prog)}
              onSwiper={updateNav}
              onSlideChange={updateNav}
              onResize={updateNav}
              className="blog-recent-swiper flex"
            >
              {blogs.map((blog, index) => (
                <SwiperSlide key={index} className="!h-auto">
                  <BlogItem blog={blog} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          <Button
            type="button"
            onClick={goNext}
            disabled={nav.isEnd}
            aria-label={t('common.slider.next')}
            style={{ top: arrowTop ?? '50%' }}
            className={clsx(
              'absolute right-1 sm:right-2 -translate-y-1/2 z-[2] w-9 h-9 sm:w-11 sm:h-11 !p-0 flex items-center justify-center bg-white border border-gray-250 shadow-md rounded-full hover:bg-white disabled:opacity-0 disabled:pointer-events-none transition-opacity',
              nav.isLocked && 'hidden'
            )}
          >
            <Icon name="chevron-end" className="text-black text-lg" />
          </Button>
        </div>

        <div className="mt-10 h-1 w-full bg-gray-250 rounded overflow-hidden">
          <div
            className="h-full bg-black transition-all duration-300"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}
